import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/stores'; // 🔥 取消注释：用于文件预览单例模式
import DynamicLink from './DynamicLink';
import CodeSandbox from './CodeSandbox';
import MermaidDiagram from './MermaidDiagram';
import ArtifactTag from './ArtifactTag';
// 🔥 FilePreview 不再在此处渲染，改为由 Chat/index.tsx 统一渲染（单例模式）
// import FilePreview from '../FilePreview';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check } from 'lucide-react';
import { useState, useMemo, useEffect, useRef } from 'react';
interface MarkdownRendererProps {
  content: string;
  className?: string;
  // 消息所属的 client，用于文件检索 API
  client?: {
    id: string | number;
    name: string;
    logo?: string;
  };
  // 是否将水平分割线作为分页符处理
  treatHrAsPageBreak?: boolean;
}
/**
 * CodeBlock Component with language tag and copy button
 */
function CodeBlock({ code, language }: { code: string; language: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };
  // 自定义样式，确保代码行背景和代码块背景一致
  const customStyle = {
    margin: 0,
    padding: '1rem',
    fontSize: '0.875rem',
    backgroundColor: '#fcfcfc',
    whiteSpace: 'normal' as const,
    // 覆盖 oneLight 主题中可能设置的行背景色
  };
  return (
    <div className="my-3 rounded-lg overflow-hidden border border-gray-300">
      {/* 标题栏 */}
      <div className="bg-gray-100 px-4 py-2 flex items-center justify-between border-b border-gray-300">
        <span className="text-xs font-semibold text-gray-600 uppercase">
          {language || 'text'}
        </span>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${
            copied
              ? 'text-green-700 bg-green-100 hover:bg-green-200'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
          }`}
          title={copied ? t('common.copied') : t('codeSandbox.copyCode')}
        >
          {copied ? (
            <>
              <Check size={14} />
              <span>{t('common.copied')}</span>
            </>
          ) : (
            <>
              <Copy size={14} />
              <span>{t('common.copy')}</span>
            </>
          )}
        </button>
      </div>
      {/* 代码内容 */}
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneLight}
        customStyle={customStyle}
        wrapLongLines={true}
        showLineNumbers={false}
        // 移除行背景色，保持统一
        lineProps={() => ({
          style: { backgroundColor: 'transparent' },
        })}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

/**
 * IsolatedHTMLContainer Component
 * 使用 Shadow DOM 渲染 HTML 内容，隔离样式避免影响全局
 */
function IsolatedHTMLContainer({ html }: { html: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    let shadowRoot = containerRef.current.shadowRoot;

    // 如果已经存在 shadow root，直接更新内容
    if (shadowRoot) {
      const wrapper = shadowRoot.querySelector('div:last-child');
      if (wrapper) {
        wrapper.innerHTML = html;
      }
      return;
    }

    // 否则创建新的 shadow root
    try {
      shadowRoot = containerRef.current.attachShadow({
        mode: 'open',
      });

      // 添加基础样式，确保内容可见
      const style = document.createElement('style');
      style.textContent = `
        :host {
          display: block;
          all: initial;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          color: #333;
          background: white;
        }
      `;
      shadowRoot.appendChild(style);

      // 创建内容包装器
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      shadowRoot.appendChild(wrapper);
    } catch (e) {
      console.error('Failed to create Shadow DOM:', e);
    }
  }, [html]);

  return (
    <div
      ref={containerRef}
      className="my-4 border border-blue-200 rounded-lg bg-blue-50 p-4"
      style={{
        display: 'block',
      }}
    />
  );
}

/**
 * 🔥 模块级 code 组件 — 引用稳定，不会随 MarkdownRenderer re-render 而重建。
 * 当 components 对象中的 code 函数引用不变时，react-markdown 不会卸载/重挂载
 * 其子树（包括 MermaidDiagram），从而避免 "re-render → mermaid 重渲染 →
 * 高度变化 → scroll 事件 → re-render" 的死循环闪动。
 */
function MarkdownCodeComponent({
  inline,
  className,
  children,
}: {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const codeString = String(children).replace(/\n$/, '');
  const hasLanguageClass = !!language;
  const hasNewlines = codeString.includes('\n');
  const isCodeBlock = inline === false || hasLanguageClass || hasNewlines;
  const isInlineCode = !isCodeBlock;
  const isReactCode = false;
  if (isReactCode) {
    return <CodeSandbox code={codeString} language={language} />;
  }
  if (isCodeBlock && language === 'mermaid') {
    return <MermaidDiagram chart={codeString} />;
  }
  if (isInlineCode) {
    return (
      <code
        className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800"
        style={{ whiteSpace: 'normal' }}
      >
        {children}
      </code>
    );
  }
  return <CodeBlock code={codeString} language={language} />;
}

/**
 * MarkdownRenderer Component
 * 渲染 Markdown 内容，支持自定义样式和特殊链接处理
 */
export default function MarkdownRenderer({
  content,
  className,
  client,
  treatHrAsPageBreak = false,
}: MarkdownRendererProps) {
  // 用于存储 artifact 数据的数组
  const [artifactsData, setArtifactsData] = useState<
    Array<{
      type: string;
      content: string;
      title: string;
      id: string;
      language?: string;
    }>
  >([]);

  // Track downloading files to show loading indicator
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set()
  );
  const downloadingFilesRef = useRef<Set<string>>(new Set());

  // 🔥 文件预览使用 store 单例模式（不再使用本地状态）
  const { setFilePreview, filePreview, setArtifactViewer } = useUIStore();
  // 🔥 用于跟踪当前组件创建的 blob URL，以便在预览关闭时清理
  const blobUrlRef = useRef<string | null>(null);

  // 🔥 清理 blob URL（当预览关闭或组件卸载时）
  useEffect(() => {
    // 如果预览被关闭且有 blob URL 需要清理
    if (!filePreview && blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, [filePreview]);

  // 组件卸载时清理 blob URL
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Selected agent (client) used for file retrieval API calls
  // const selectedAgent = useUIStore(state => state.selectedAgent);

  // 清理内容：移除不应该渲染的 XML 标签
  // 这些标签通常来自 AI 的函数调用格式，不应该显示给用户
  const cleanContent = (
    text: string,
    artifactsCollector: Array<{
      type: string;
      content: string;
      title: string;
      id: string;
      language?: string;
    }>
  ): string => {
    // HTML 实体解码函数
    const decodeHTMLEntities = (str: string): string => {
      return str
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&#x27;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&amp;/g, '&')
        .replace(/&nbsp;/g, ' ')
        .replace(/\\n/g, '\n') // 处理 \n 字符串
        .replace(/&mdash;/g, '-'); // 处理长破折号
    };
    let cleaned = text;

    // 🔥 标准化 c.function_calls / c.invoke / c.parameter 标签
    // 将带 c. 前缀的标签统一替换为无前缀版本，确保后续所有正则和逻辑兼容
    cleaned = cleaned
      .replace(/<(\/?)c\.function_calls/gi, '<$1function_calls')
      .replace(/<(\/?)c\.invoke/gi, '<$1invoke')
      .replace(/<(\/?)c\.parameter/gi, '<$1parameter');

    let hasIncompleteTag = false;
    const tagsToCheck = [
      // 'result',
      'function_calls',
      'invoke',
      'parameter',
      'artifacts',
      'artifact',
    ];

    // 将 markdown 代码块、内联代码以及 pre/code 标签内容替换为占位符，避免后续标签检测误伤
    const maskCodeLikeSegments = (input: string) => {
      const placeholders: string[] = [];
      const replaceWithPlaceholder = (match: string) => {
        const index = placeholders.length;
        placeholders.push(match);
        return `__CODE_LIKE_PLACEHOLDER_${index}__`;
      };

      let masked = input;
      // 多行代码块 ``` ```
      masked = masked.replace(/```[\s\S]*?```/g, replaceWithPlaceholder);
      // 外层 4 个以上反引号的代码块
      masked = masked.replace(
        /^`{4,}[\s\S]*?^`{4,}$/gm,
        replaceWithPlaceholder
      );
      // 内联代码 `...`
      masked = masked.replace(/`[^`\n]+?`/g, replaceWithPlaceholder);
      // HTML pre/code 标签
      masked = masked.replace(/<pre[\s\S]*?<\/pre>/gi, replaceWithPlaceholder);
      masked = masked.replace(
        /<code[\s\S]*?<\/code>/gi,
        replaceWithPlaceholder
      );

      return { masked, placeholders };
    };

    // ===== 前置步骤：检测并移除不完整的标签 =====
    // 检测每个关键标签是否有完整的开闭标签对
    // 特殊处理：对于 result 标签，检测是否有未闭合的开标签
    // 在进行标签检测前先屏蔽代码相关片段
    const { masked: maskedForTags, placeholders: codePlaceholders } =
      maskCodeLikeSegments(cleaned);
    let working = maskedForTags;

    const resultOpenCount = (working.match(/<result[^>]*>/gi) || []).length;
    const resultCloseCount = (working.match(/<\/result>/gi) || []).length;

    if (resultOpenCount > resultCloseCount) {
      hasIncompleteTag = true;
      // 移除从最后一个 <result 开始到文尾的所有内容
      working = working.replace(/<result[^>]*>[\s\S]*$/i, '');
    }

    // 检查其他标签
    for (const tag of tagsToCheck) {
      if (tag === 'result') continue; // 已经单独处理了

      const closeTag = `</${tag}>`;
      const openCount = (working.match(new RegExp(`<${tag}[^>]*>`, 'gi')) || [])
        .length;
      const closeCount = (working.match(new RegExp(closeTag, 'gi')) || [])
        .length;

      // 如果开闭标签数不相等，说明有不完整的标签
      if (openCount !== closeCount) {
        hasIncompleteTag = true;
        // 移除所有不完整的该标签及其内容
        // 策略：从最后一个开标签开始到文尾的内容都视为残缺，移除掉
        working = working.replace(
          new RegExp(`<${tag}[^>]*>[\\s\\S]*$`, 'i'),
          ''
        );
      }
    }

    // 恢复代码相关占位符
    // 🔥 修复：必须使用函数替换而非字符串替换！
    // 字符串替换中 $' 会被解释为"匹配位置之后的所有文本"，
    // 当代码块包含 $' (如 Python 正则 r'.*$') 时会导致内容被指数级复制（1000x+），页面卡死。
    codePlaceholders.forEach((original, index) => {
      working = working.replace(
        new RegExp(`__CODE_LIKE_PLACEHOLDER_${index}__`, 'g'),
        () => original
      );
    });

    // 将 working 回写到 cleaned
    cleaned = working;

    // 如果发现不完整标签，添加提示
    if (hasIncompleteTag) {
      cleaned =
        cleaned.trim() +
        '\n\n⚠️ *Note: Incomplete tag structure detected. Malformed content has been removed.*';
    }

    // ===== 预处理步骤：转换 Markdown 链接为 HTML 链接 =====
    // 这样可以确保即使在 HTML 和 Markdown 混合内容中，
    // 所有链接都会被一致地处理
    // 🔥 改进正则：支持 URL 中包含括号的情况，如 Issue List (49).csv
    // 原来的 ([^)]+) 会在遇到 URL 中的第一个 ) 时提前终止匹配
    // 新正则 ([^()]*(?:\([^()]*\)[^()]*)*) 可以正确处理嵌套括号
    cleaned = cleaned.replace(
      /\[([^\]]+)\]\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g,
      '<a href="$2">$1</a>'
    );

    // ===== 第一步：智能保护策略 =====
    const outerCodeBlocks: string[] = [];
    const codeBlocks: string[] = [];
    const inlineCodes: string[] = [];

    // 检测是否有包含 type/content 参数的 invoke（artifact 内容）
    // 同时检测 <antArtifact> / <artifact> 标签，避免内联代码保护步骤
    // 将 artifact 中的模板字面量（如 `${v} 万元`）误替换为占位符
    const hasArtifactInvoke =
      /<invoke[^>]*>[\s\S]*?<parameter[^>]*name=["'](?:type|content)["']/.test(
        text
      ) || /<(?:ant)?[aA]rtifact\b/.test(text);

    if (!hasArtifactInvoke) {
      // 纯 markdown 内容（如 res2.txt），先保护所有代码块
      // 1. 保护 4+ 个反引号的外层代码块
      cleaned = cleaned.replace(/^`{4,}[\s\S]*?^`{4,}$/gm, match => {
        const index = outerCodeBlocks.length;
        outerCodeBlocks.push(match);
        return `__OUTER_CODE_BLOCK_PLACEHOLDER_${index}__`;
      });

      // 2. 保护 3 个反引号的代码块
      cleaned = cleaned.replace(/```[\s\S]*?```/g, match => {
        const index = codeBlocks.length;
        codeBlocks.push(match);
        return `__CODE_BLOCK_PLACEHOLDER_${index}__`;
      });

      // 3. 保护内联代码
      cleaned = cleaned.replace(/`[^`\n]+?`/g, match => {
        const index = inlineCodes.length;
        inlineCodes.push(match);
        return `__INLINE_CODE_PLACEHOLDER_${index}__`;
      });
    }

    // ===== 第二步：处理 artifact 和 invoke 标签 =====
    // 在保护代码块和内联代码之前，先提取所有 artifact 内容
    // 这样 artifact 中的反引号模板字符串不会被替换成占位符

    // -1. 首先过滤掉 <antThinking> 标签（Claude API 返回的思考过程标签）
    cleaned = cleaned.replace(/<antThinking[\s\S]*?<\/antThinking>/gi, '');

    // 0. 处理 <antArtifact> 和 <artifact> 标签格式 - 都使用 ArtifactViewer 分屏渲染
    // 匹配 <antArtifact> 和 <artifact> type="application/vnd.ant.react">...</artifact>
    cleaned = cleaned.replace(
      /<(?:ant)?[aA]rtifact([^>]*)type=["']application\/vnd\.ant\.react["']([^>]*)>([\s\S]*?)<\/(?:ant)?[aA]rtifact>/gi,
      (match, _before, _after, content) => {
        const reactCode = content.trim();
        const id = `artifact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // 尝试提取 title 或 identifier 属性
        const titleMatch = match.match(
          /(?:title|identifier)=["']([^"']+)["']/i
        );
        const title = titleMatch ? titleMatch[1] : 'React Component';
        const encodedTitle = btoa(encodeURIComponent(title));
        artifactsCollector.push({
          type: 'jsx',
          content: reactCode,
          title,
          id,
        });
        return `\n{{ARTIFACT_PLACEHOLDER::${id}::jsx::${encodedTitle}}}\n`;
      }
    );
    // 匹配 <antArtifact> 和 <artifact> type="application/vnd.ant.code" language="...">...</artifact>
    cleaned = cleaned.replace(
      /<(?:ant)?[aA]rtifact([^>]*)type=["']application\/vnd\.ant\.code["']([^>]*)language=["']([^"']+)["']([^>]*)>([\s\S]*?)<\/(?:ant)?[aA]rtifact>/gi,
      (match, _before, _middle, language, _after, content) => {
        const codeContent = content.trim();
        const id = `artifact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // 尝试提取 title 或 identifier 属性
        const titleMatch = match.match(
          /(?:title|identifier)=["']([^"']+)["']/i
        );
        const title = titleMatch
          ? titleMatch[1]
          : `${language.toUpperCase()} Code`;
        const encodedTitle = btoa(encodeURIComponent(title));
        artifactsCollector.push({
          type: 'code',
          content: codeContent,
          title,
          id,
          language,
        });
        return `\n{{ARTIFACT_PLACEHOLDER::${id}::code::${encodedTitle}::${language}}}\n`;
      }
    );
    // 匹配 <antArtifact> 和 <artifact> type="text/markdown">...</artifact>
    cleaned = cleaned.replace(
      /<(?:ant)?[aA]rtifact([^>]*)type=["']text\/markdown["']([^>]*)>([\s\S]*?)<\/(?:ant)?[aA]rtifact>/gi,
      (match, _before, _after, content) => {
        let markdownContent = content.trim();
        markdownContent = decodeHTMLEntities(markdownContent);
        const id = `artifact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // 尝试提取 title 或 identifier 属性
        const titleMatch = match.match(
          /(?:title|identifier)=["']([^"']+)["']/i
        );
        const title = titleMatch ? titleMatch[1] : 'Markdown Document';
        const encodedTitle = btoa(encodeURIComponent(title));
        artifactsCollector.push({
          type: 'markdown',
          content: markdownContent,
          title,
          id,
        });
        return `\n{{ARTIFACT_PLACEHOLDER::${id}::markdown::${encodedTitle}}}\n`;
      }
    );
    // 匹配 <antArtifact> 和 <artifact> type="application/vnd.ant.mermaid">...</artifact>
    cleaned = cleaned.replace(
      /<(?:ant)?[aA]rtifact([^>]*)type=["']application\/vnd\.ant\.mermaid["']([^>]*)>([\s\S]*?)<\/(?:ant)?[aA]rtifact>/gi,
      (match, _before, _after, content) => {
        let mermaidContent = content.trim();
        mermaidContent = decodeHTMLEntities(mermaidContent);
        const id = `artifact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // 尝试提取 title 或 identifier 属性
        const titleMatch = match.match(
          /(?:title|identifier)=["']([^"']+)["']/i
        );
        const title = titleMatch ? titleMatch[1] : 'Mermaid Diagram';
        const encodedTitle = btoa(encodeURIComponent(title));
        artifactsCollector.push({
          type: 'mermaid',
          content: mermaidContent,
          title,
          id,
        });
        return `\n{{ARTIFACT_PLACEHOLDER::${id}::mermaid::${encodedTitle}}}\n`;
      }
    );
    // 匹配 <antArtifact> 和 <artifact> type="text/html">...</artifact>
    cleaned = cleaned.replace(
      /<(?:ant)?[aA]rtifact([^>]*)type=["']text\/html["']([^>]*)>([\s\S]*?)<\/(?:ant)?[aA]rtifact>/gi,
      (match, _before, _after, content) => {
        let htmlContent = content.trim();
        htmlContent = decodeHTMLEntities(htmlContent);
        const id = `artifact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // 尝试提取 title 或 identifier 属性
        const titleMatch = match.match(
          /(?:title|identifier)=["']([^"']+)["']/i
        );
        const title = titleMatch ? titleMatch[1] : 'HTML Content';
        const encodedTitle = btoa(encodeURIComponent(title));
        artifactsCollector.push({
          type: 'html',
          content: htmlContent,
          title,
          id,
        });
        return `\n{{ARTIFACT_PLACEHOLDER::${id}::html::${encodedTitle}}}\n`;
      }
    );
    // 匹配 <antArtifact> 和 <artifact> type="image/svg+xml">...</artifact>
    cleaned = cleaned.replace(
      /<(?:ant)?[aA]rtifact([^>]*)type=["']image\/svg\+xml["']([^>]*)>([\s\S]*?)<\/(?:ant)?[aA]rtifact>/gi,
      (match, _before, _after, content) => {
        let svgContent = content.trim();
        svgContent = decodeHTMLEntities(svgContent);
        const id = `artifact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // 尝试提取 title 或 identifier 属性
        const titleMatch = match.match(
          /(?:title|identifier)=["']([^"']+)["']/i
        );
        const title = titleMatch ? titleMatch[1] : 'SVG Image';
        const encodedTitle = btoa(encodeURIComponent(title));
        artifactsCollector.push({
          type: 'svg',
          content: svgContent,
          title,
          id,
        });
        return `\n{{ARTIFACT_PLACEHOLDER::${id}::svg::${encodedTitle}}}\n`;
      }
    );
    // 移除 <artifacts> 和 <antArtifacts> 包装标签
    cleaned = cleaned.replace(/<\/?(?:ant)?[aA]rtifacts>/gi, '');

    // 统一处理所有包含 type 和 content 参数的块
    // 不管是否在 function_calls 或 invoke 标签中
    // 首先找到所有的 invoke 块，然后在每个块内提取 type 和 content
    cleaned = cleaned.replace(
      /<invoke[^>]*>[\s\S]*?<\/invoke>/gi,
      invokeBlock => {
        // 在这个 invoke 块内查找 type 参数（支持 type 和 artifact_type）
        const typeMatch = invokeBlock.match(
          /<parameter[^>]*name=["'](?:artifact_)?type["'][^>]*>([\s\S]*?)<\/parameter>/i
        );
        // 在这个 invoke 块内查找 content 参数
        const contentMatch = invokeBlock.match(
          /<parameter[^>]*name=["']content["'][^>]*>([\s\S]*?)<\/parameter>/i
        );
        // 在这个 invoke 块内查找 title/name/id 参数（可选，支持 name, title, id）
        // const titleMatch = invokeBlock.match(
        //   /<parameter[^>]*name=["'](?:title|name|id)["'][^>]*>([\s\S]*?)<\/parameter>/i
        // );
        const titleMatch =
          invokeBlock.match(
            /<parameter[^>]*name=["']title["'][^>]*>([\s\S]*?)<\/parameter>/i
          ) ||
          invokeBlock.match(
            /<parameter[^>]*name=["']name["'][^>]*>([\s\S]*?)<\/parameter>/i
          ) ||
          invokeBlock.match(
            /<parameter[^>]*name=["']id["'][^>]*>([\s\S]*?)<\/parameter>/i
          );

        // 如果没有 content，移除整个块
        if (!contentMatch) {
          return '';
        }

        // 清理 type 和 content
        const cleanType = typeMatch ? typeMatch[1].trim() : '';
        let cleanContent = contentMatch[1].trim();
        const title = titleMatch
          ? decodeHTMLEntities(titleMatch[1].trim())
          : 'Untitled';

        // 根据 type 处理 - 所有类型都使用 ArtifactViewer 分屏渲染
        const id = `artifact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const encodedTitle = btoa(encodeURIComponent(title));

        if (cleanType === 'text/markdown') {
          cleanContent = decodeHTMLEntities(cleanContent);
          artifactsCollector.push({
            type: 'markdown',
            content: cleanContent,
            title,
            id,
          });
          return `\n{{ARTIFACT_PLACEHOLDER::${id}::markdown::${encodedTitle}}}\n`;
        } else if (cleanType === 'application/vnd.ant.react') {
          // React/JSX 代码：不要解码 HTML 实体！
          // 因为 ArtifactViewer 会将 &lt; &gt; 转换为 JSX 表达式
          artifactsCollector.push({
            type: 'jsx',
            content: cleanContent,
            title,
            id,
          });
          return `\n{{ARTIFACT_PLACEHOLDER::${id}::jsx::${encodedTitle}}}\n`;
        } else if (cleanType === 'application/vnd.ant.code') {
          // 代码块：提取 language
          const languageMatch = invokeBlock.match(
            /<parameter[^>]*name=["']language["'][^>]*>([\s\S]*?)<\/parameter>/i
          );
          const language = languageMatch ? languageMatch[1].trim() : 'text';
          artifactsCollector.push({
            type: 'code',
            content: cleanContent,
            title,
            id,
            language,
          });
          return `\n{{ARTIFACT_PLACEHOLDER::${id}::code::${encodedTitle}::${language}}}\n`;
        } else if (cleanType === 'application/vnd.ant.mermaid') {
          cleanContent = decodeHTMLEntities(cleanContent);
          artifactsCollector.push({
            type: 'mermaid',
            content: cleanContent,
            title,
            id,
          });
          return `\n{{ARTIFACT_PLACEHOLDER::${id}::mermaid::${encodedTitle}}}\n`;
        } else if (
          cleanType === 'text/html' ||
          cleanType === 'application/javascript+html'
        ) {
          artifactsCollector.push({
            type: 'html',
            content: cleanContent,
            title,
            id,
          });
          return `\n{{ARTIFACT_PLACEHOLDER::${id}::html::${encodedTitle}}}\n`;
        } else if (cleanType.startsWith('image/svg')) {
          cleanContent = decodeHTMLEntities(cleanContent);
          artifactsCollector.push({
            type: 'svg',
            content: cleanContent,
            title,
            id,
          });
          return `\n{{ARTIFACT_PLACEHOLDER::${id}::svg::${encodedTitle}}}\n`;
        } else if (cleanType === 'text/plain' || !cleanType) {
          // 纯文本（如小说）
          cleanContent = decodeHTMLEntities(cleanContent);
          artifactsCollector.push({
            type: 'text',
            content: cleanContent,
            title,
            id,
          });
          return `\n{{ARTIFACT_PLACEHOLDER::${id}::text::${encodedTitle}}}\n`;
        } else {
          // 默认：所有未知类型也作为文本处理并使用分屏渲染
          cleanContent = decodeHTMLEntities(cleanContent);
          artifactsCollector.push({
            type: 'text',
            content: cleanContent,
            title,
            id,
          });
          return `\n{{ARTIFACT_PLACEHOLDER::${id}::text::${encodedTitle}}}\n`;
        }
      }
    );

    // ===== 第三步：现在保护剩余的代码块和内联代码（artifact 已提取）=====

    // 保护 3 个反引号的代码块
    cleaned = cleaned.replace(/```[\s\S]*?```/g, match => {
      const index = codeBlocks.length;
      codeBlocks.push(match);
      return `__CODE_BLOCK_PLACEHOLDER_${index}__`;
    });

    // 保护内联代码
    cleaned = cleaned.replace(/`[^`\n]+?`/g, match => {
      const index = inlineCodes.length;
      inlineCodes.push(match);
      return `__INLINE_CODE_PLACEHOLDER_${index}__`;
    });

    // ===== 第四步：移除 XML 函数调用标签（代码块已经被保护了）=====
    // 移除其他 XML 函数调用标签（已经在前面通过占位符保护了代码块和内联代码）
    // 只移除完整的、配对的、不在代码块中的 XML 标签
    cleaned = cleaned
      .replace(/<function_calls>/gi, '')
      .replace(/<\/function_calls>/gi, '')
      .replace(/<thinking[\s\S]*?<\/thinking>/gi, '')
      .replace(/<antThinking[\s\S]*?<\/antThinking>/gi, '')
      // 移除任何未处理的 artifact 和 antArtifact 标签
      .replace(/<(?:ant)?[aA]rtifact[\s\S]*?<\/(?:ant)?[aA]rtifact>/gi, '')
      // 提取 <result> 标签内的内容，保留内容但移除标签
      .replace(/<result>([\s\S]*?)<\/result>/gi, '$1')
      // 移除其他单独的 XML 标签
      .replace(/<\/?call>/gi, '')
      .replace(/<\/?think>/gi, '');

    // 最后，将特殊标记转换为实际的占位符 HTML
    cleaned = cleaned.replace(
      /\{\{ARTIFACT_PLACEHOLDER::([^:]+)::([^:]+)::([^}]+?)(?:::([^}]+))?\}\}/g,
      (_match, id, type, encodedTitle, language) => {
        // 解码 title
        const title = decodeURIComponent(atob(encodedTitle));
        const languageAttr = language ? ` data-language="${language}"` : '';
        return `<div class="artifact-placeholder" data-id="${id}" data-type="${type}" data-title="${title}"${languageAttr}></div>`;
      }
    );
    // 3. 转换 HTML <pre><code> 为 Markdown 代码块
    // 只处理真正的代码块，不影响其他HTML内容
    // 注意：不自动检测 React 代码，防止误识别普通代码为可执行的 React 组件
    cleaned = cleaned.replace(
      /<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi,
      (_match, codeContent) => {
        // 解码 HTML 实体
        let decodedCode = decodeHTMLEntities(codeContent).trim();
        // 清理可能的多余空白和特殊字符
        decodedCode = decodedCode.replace(/^\s+/gm, match => {
          // 保留缩进但移除过多空白
          return match.length > 20 ? '' : match;
        });
        // 始终转换为普通代码块，不自动转换为 JSX artifact
        // 这样可以避免将文件内容（如 res.txt）中的代码误识别为可执行组件
        return `\n\`\`\`\n${decodedCode}\n\`\`\`\n`;
      }
    );
    // 4. 只移除 HTML 标签前的缩进，避免被识别为代码块
    // 保留正常代码块（非 HTML 标签）的缩进
    cleaned = cleaned.replace(/^[ \t]+(<[a-zA-Z/!])/gm, '$1');

    // ===== 第五步：检测并隔离可能影响全局的 HTML 内容（包含 style 标签）=====
    // const dangerousHTMLPattern = /<(?:style|script|link)[\s>]/i;
    const hasCompleteHTML =
      /<html[\s>]/i.test(cleaned) ||
      (/<head[\s>]/i.test(cleaned) && /<body[\s>]/i.test(cleaned));

    // if (hasCompleteHTML || dangerousHTMLPattern.test(cleaned)) {
    if (hasCompleteHTML) {
      console.warn('检测到完整 HTML 文档或危险标签，使用隔离渲染');

      // 创建一个特殊的 marker，用于在 Markdown 渲染后识别和替换成 iframe
      const id = `isolated-html-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      // 使用浏览器原生 API 进行 base64 编码
      const encodedHTML = btoa(unescape(encodeURIComponent(cleaned)));

      // 用一个特殊的 HTML 注释标记来保存 HTML 内容，并用 iframe 显示
      cleaned = `<div data-isolated-html="${id}" data-content="${encodedHTML}" style="display:none;"></div>`;
    }

    // ===== 最后一步：恢复所有代码块和内联代码 =====
    // 恢复内层代码块（3 个反引号）
    // 重要：双重转义代码块内的 < 和 >，防止 rehypeRaw 插件解码后再当作 HTML 标签
    cleaned = cleaned.replace(
      /__CODE_BLOCK_PLACEHOLDER_(\d+)__/g,
      (_, index) => {
        const block = codeBlocks[parseInt(index)];
        // 直接返回原始代码块，ReactMarkdown 会正确处理
        return block;
      }
    );

    // 恢复内联代码
    cleaned = cleaned.replace(
      /__INLINE_CODE_PLACEHOLDER_(\d+)__/g,
      (_, index) => {
        return inlineCodes[parseInt(index)];
      }
    );

    // 恢复外层代码块（4+ 个反引号），处理成标准 3 个反引号
    cleaned = cleaned.replace(
      /__OUTER_CODE_BLOCK_PLACEHOLDER_(\d+)__/g,
      (_, index) => {
        const original = outerCodeBlocks[parseInt(index)];
        // 将 4+ 个反引号转换为标准的 3 个反引号
        return original
          .replace(/^`{4,}(\w+)/gm, '```$1')
          .replace(/^`{4,}$/gm, '```');
      }
    );

    // 转换 HTML 注释格式的占位符为 div 元素
    cleaned = cleaned.replace(
      /<!--ARTIFACT_PLACEHOLDER::([^:]+)::([^:]+)::([^-]+?)(?:::([^-]+?))?-->/g,
      (_match, id, type, encodedTitle, language) => {
        try {
          // 解码 title
          const title = decodeURIComponent(atob(encodedTitle));
          const languageAttr = language ? ` data-language="${language}"` : '';
          return `<div class="artifact-placeholder" data-id="${id}" data-type="${type}" data-title="${title}"${languageAttr}></div>`;
        } catch (e) {
          console.error('Error converting placeholder:', e);
          return _match; // 如果转换失败，保留原占位符
        }
      }
    );

    // 🔥 处理所有脚注格式，避免 remark-gfm 的脚注解析问题
    // 1. 列表中的脚注定义（非标准格式 [^xxx^]:）：保留数字
    //    "- [^1_1^]: [链接](URL)" -> "- [1] [链接](URL)"
    cleaned = cleaned.replace(/- \[\^(\d+)[^\]^]*\^\]: /g, '- [$1] ');

    // 2. 列表中的脚注定义（标准格式 [^xxx]:）：保留数字
    //    "- [^1]: [链接](URL)" -> "- [1] [链接](URL)"
    cleaned = cleaned.replace(/- \[\^(\d+)\]: /g, '- [$1] ');

    // 3. 文本中的脚注引用（非标准格式 [^xxx^]）：提取第一个数字，蓝色上标
    //    "[^1_1^]" -> "<sup style='color:#3b82f6'>[1]</sup>"
    cleaned = cleaned.replace(
      /\[\^(\d+)[^\]^]*\^\]/g,
      '<sup style="color:#3b82f6">[$1]</sup>'
    );

    // 4. 文本中的脚注引用（标准格式 [^xxx]）：提取数字，蓝色上标
    //    "[^1]" -> "<sup style='color:#3b82f6'>[1]</sup>"
    cleaned = cleaned.replace(
      /\[\^(\d+)\]/g,
      '<sup style="color:#3b82f6">[$1]</sup>'
    );

    return cleaned;
  };

  // Use useMemo to avoid re-running cleanContent on every render
  const { cleanedContent, artifacts } = useMemo(() => {
    const tempArtifactsCollector: Array<{
      type: string;
      content: string;
      title: string;
      id: string;
      language?: string;
    }> = [];

    // Strip wrapper tags (<result>, <function_calls>) before processing
    // This prevents rehypeRaw from trying to render them as HTML elements
    let processedContent = content;

    // Debug: Log first 500 chars of original content

    // Remove <result> wrapper (opening and closing tags)
    processedContent = processedContent.replace(/<result>\s*/gi, '');
    processedContent = processedContent.replace(/\s*<\/result>/gi, '');

    // Remove <function_calls> wrapper (opening and closing tags)
    // processedContent = processedContent.replace(/<function_calls>\s*/gi, '');
    // processedContent = processedContent.replace(/\s*<\/function_calls>/gi, '');

    const cleaned = cleanContent(processedContent, tempArtifactsCollector);
    return { cleanedContent: cleaned, artifacts: tempArtifactsCollector };
  }, [content]);

  // Update artifacts data only when artifacts change
  useEffect(() => {
    if (artifacts.length > 0) {
      setArtifactsData(artifacts);
    }
  }, [artifacts]);

  return (
    <>
      <div className={cn('text-gray-800', className)}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeRaw, [rehypeKatex, { strict: false }]]}
          skipHtml={false}
          components={{
            p: ({ children }: { children?: React.ReactNode }) => (
              <div className="mb-3 last:mb-0 leading-relaxed break-words">
                {children}
              </div>
            ),
            ul: ({ children }: { children?: React.ReactNode }) => (
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                {children}
              </ul>
            ),
            ol: ({ children }: { children?: React.ReactNode }) => (
              <ol className="list-decimal list-inside mb-3 space-y-1 ml-4">
                {children}
              </ol>
            ),
            li: ({ children }: { children?: React.ReactNode }) => (
              <li className="leading-relaxed break-words">{children}</li>
            ),
            h1: ({ children }: { children?: React.ReactNode }) => (
              <h1 className="text-xl font-semibold mb-3 mt-6 first:mt-0 text-gray-900 break-words">
                {children}
              </h1>
            ),
            h2: ({ children }: { children?: React.ReactNode }) => (
              <h2 className="text-lg font-semibold mb-3 mt-5 first:mt-0 text-gray-900 break-words">
                {children}
              </h2>
            ),
            h3: ({ children }: { children?: React.ReactNode }) => (
              <h3 className="text-base font-semibold mb-2 mt-4 first:mt-0 text-gray-900 break-words">
                {children}
              </h3>
            ),
            blockquote: ({ children }: { children?: React.ReactNode }) => (
              <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3 text-gray-600">
                {children}
              </blockquote>
            ),
            // 🔥 使用模块级稳定引用，防止 re-render 时函数引用变化
            // 导致 react-markdown 卸载/重挂载 MermaidDiagram 子树
            code: MarkdownCodeComponent,
            hr: () =>
              treatHrAsPageBreak ? (
                <div className="my-4 flex items-center gap-2">
                  <hr className="flex-1 border-t border-gray-300" />
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                    Page Break
                  </span>
                  <hr className="flex-1 border-t border-gray-300" />
                </div>
              ) : (
                <hr className="my-4 border-t border-gray-300" />
              ),
            table: ({ children }: { children?: React.ReactNode }) => (
              <table className="border-collapse border border-gray-300 my-3 mx-auto">
                {children}
              </table>
            ),
            thead: ({ children }: { children?: React.ReactNode }) => (
              <thead className="bg-gray-50">{children}</thead>
            ),
            tbody: ({ children }: { children?: React.ReactNode }) => (
              <tbody className="[&>tr:nth-child(odd)]:bg-white [&>tr:nth-child(even)]:bg-gray-50">
                {children}
              </tbody>
            ),
            th: ({ children }: { children?: React.ReactNode }) => (
              <th className="border border-gray-300 p-[0.28cm] bg-gray-100 font-semibold text-left">
                {children}
              </th>
            ),
            td: ({ children }: { children?: React.ReactNode }) => (
              <td className="border border-gray-300 p-[0.28cm]">{children}</td>
            ),
            a: ({
              href,
              children,
              id,
              ['data-pdf-url']: pdfUrl,
              ['data-pdf-filename']: pdfFilename,
            }: any) => {
              // 如果只有 id 没有 href，说明是导航锚点，直接渲染
              if (id && !href) {
                return <a id={id}>{children}</a>;
              }

              // 处理 # 开头的内部锚点链接（避免哈希路由跳转新页面）
              if (href && href.startsWith('#')) {
                const handleAnchorClick = (e: React.MouseEvent) => {
                  e.preventDefault();
                  const targetId = href.slice(1); // 移除 # 前缀
                  const targetElement = document.getElementById(targetId);
                  if (targetElement) {
                    targetElement.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }
                };
                return (
                  <a
                    href={href}
                    onClick={handleAnchorClick}
                    style={{
                      color: '#0066cc',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {children}
                  </a>
                );
              }

              // If backend provided explicit pdf data attributes, use the existing PDF open flow
              if (pdfUrl) {
                return (
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      try {
                        if ((window as any).openPDFInNewTab) {
                          (window as any).openPDFInNewTab(
                            pdfUrl,
                            pdfFilename || 'document.pdf'
                          );
                        }
                      } catch (error) {
                        console.error('Error opening PDF:', error);
                      }
                    }}
                    style={{
                      color: '#0066cc',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {children}
                  </a>
                );
              }

              // Handle special /copilot-file links returned by backend
              try {
                if (href && href.startsWith('/copilot-file')) {
                  // 🔥 修复：预处理 href，将查询参数中的 # 编码为 %23
                  // 避免 new URL() 将 # 解释为片段标识符（fragment）而截断文件名
                  // 例如：文件名 "测试Test_@#$$%.docx" 中的 # 会导致后面的内容被丢弃
                  const qIdx = href.indexOf('?');
                  const safeHref =
                    qIdx >= 0
                      ? href.substring(0, qIdx + 1) +
                        href.substring(qIdx + 1).replace(/#/g, '%23')
                      : href;
                  const url = new URL(safeHref, window.location.origin);
                  // file may be in param `file` or `filePath`
                  const originalFile =
                    url.searchParams.get('file') ||
                    url.searchParams.get('filePath') ||
                    '';
                  // 🔥 获取 projectId，用于支持文件编辑
                  const projectId = url.searchParams.get('projectId');
                  // const lower = originalFile.toLowerCase();
                  // If it's a PDF, delegate to DynamicLink logic
                  // if (lower.endsWith('.pdf')) {
                  //   return <DynamicLink href={href}>{children}</DynamicLink>;
                  // }

                  // 检查文件类型是否支持预览
                  const shouldPreview = (filename: string): boolean => {
                    const ext = '.' + filename.toLowerCase().split('.').pop();
                    const previewableExts = [
                      // 代码文件
                      '.js',
                      '.jsx',
                      '.ts',
                      '.tsx',
                      '.py',
                      '.java',
                      '.cpp',
                      '.c',
                      '.cs',
                      '.php',
                      '.rb',
                      '.go',
                      '.rs',
                      '.swift',
                      '.kt',
                      '.scala',
                      '.r',
                      '.sas',
                      '.sh',
                      '.bash',
                      '.sql',
                      '.vue',
                      // 数据和配置文件
                      '.rda',
                      '.rdata',
                      '.rds',
                      '.csv',
                      '.tsv',
                      '.json',
                      '.xml',
                      '.yaml',
                      '.yml',
                      '.toml',
                      '.ini',
                      '.conf',
                      // 标记语言
                      '.html',
                      '.htm',
                      '.md',
                      '.markdown',
                      '.txt',
                      '.log',
                      // 图片文件
                      '.jpg',
                      '.jpeg',
                      '.png',
                      '.gif',
                      '.bmp',
                      '.webp',
                      '.svg',
                      '.ico',
                      '.tiff',
                      '.tif',
                      // PDF 文件
                      '.pdf',
                      // Word 文档（支持 mammoth 预览）
                      '.doc',
                      '.docx',
                    ];
                    return previewableExts.includes(ext);
                  };

                  // 检查是否是 Office 文件（直接下载）
                  // 🔥 注意：.doc/.docx 已移至 previewableExts，支持 mammoth 预览
                  const isOfficeFile = (filename: string): boolean => {
                    const ext = '.' + filename.toLowerCase().split('.').pop();
                    return [
                      '.xls',
                      '.xlsx',
                      '.ppt',
                      '.pptx',
                      '.odt',
                      '.ods',
                      '.odp',
                    ].includes(ext);
                  };

                  const handleClick = async (e: React.MouseEvent) => {
                    e.preventDefault();

                    // 如果 originalFile 是文件夹（没有扩展名或路径以斜杠结尾），直接返回不处理
                    const filename =
                      (originalFile || '').split(/[\\/]/).pop() || '';
                    if (
                      !filename ||
                      !filename.includes('.') ||
                      originalFile.endsWith('/') ||
                      originalFile.endsWith('\\')
                    ) {
                      return;
                    }

                    // Office 文件直接下载
                    if (isOfficeFile(originalFile)) {
                      handleDownload();
                      return;
                    }

                    // 支持预览的文件打开预览
                    if (shouldPreview(originalFile)) {
                      try {
                        // 优先使用消息本身的 client，其次使用全局 selectedAgent
                        const clientId = client?.id;
                        if (!clientId) {
                          console.error(
                            'No client available for file retrieval'
                          );
                          return;
                        }

                        const token = localStorage.getItem('user-token') || '';
                        const originalParams = new URLSearchParams(url.search);
                        if (token) {
                          originalParams.set('Authorization', token);
                        }

                        const apiUrl = `/api/clients/${clientId}/files/retrieve?${originalParams.toString()}`;
                        const filename =
                          (originalFile || '').split(/[\\/]/).pop() || 'file';

                        // 对于 PDF 文件，需要先 fetch 为 blob 再创建 object URL
                        // 因为直接使用 API URL 会触发下载（服务端返回 Content-Disposition: attachment）
                        const ext =
                          '.' + filename.toLowerCase().split('.').pop();
                        if (ext === '.pdf') {
                          const response = await fetch(apiUrl);
                          if (!response.ok) {
                            throw new Error(
                              `Failed to fetch file: ${response.statusText}`
                            );
                          }
                          const blob = await response.blob();
                          // 确保 blob 具有正确的 PDF MIME 类型
                          const pdfBlob = new Blob([blob], {
                            type: 'application/pdf',
                          });
                          const blobUrl = URL.createObjectURL(pdfBlob);
                          // 🔥 存储 blob URL 以便后续清理
                          blobUrlRef.current = blobUrl;
                          // 🔥 打开文件预览时关闭 Artifact 分屏（互斥）
                          setArtifactViewer(null);
                          // 🔥 使用 store 单例模式设置预览
                          setFilePreview({
                            fileUrl: blobUrl,
                            filename: filename,
                            // 🔥 传递 projectId、filePath 和 clientId，支持文件编辑
                            projectId: projectId || undefined,
                            filePath: originalFile || undefined,
                            clientId: clientId ? String(clientId) : undefined,
                          });
                        } else {
                          // 🔥 打开文件预览时关闭 Artifact 分屏（互斥）
                          setArtifactViewer(null);
                          // 🔥 其他文件直接使用 API URL（使用 store 单例模式）
                          setFilePreview({
                            fileUrl: apiUrl,
                            filename: filename,
                            // 🔥 传递 projectId、filePath 和 clientId，支持文件编辑
                            projectId: projectId || undefined,
                            filePath: originalFile || undefined,
                            clientId: clientId ? String(clientId) : undefined,
                          });
                        }
                      } catch (err) {
                        console.error('File preview error', err);
                      }
                      return;
                    }

                    // 其他文件直接下载
                    handleDownload();
                  };

                  const handleDownload = async () => {
                    try {
                      // 优先使用消息本身的 client，其次使用全局 selectedAgent
                      const clientId = client?.id;
                      if (!clientId) {
                        console.error('No client available for file retrieval');
                        return;
                      }

                      // Set loading state for this file
                      const fileKey = originalFile || href;
                      downloadingFilesRef.current.add(fileKey);
                      setDownloadingFiles(new Set(downloadingFilesRef.current));

                      // Append Authorization query param using token from localStorage
                      const token = localStorage.getItem('user-token') || '';
                      const originalParams = new URLSearchParams(url.search);
                      if (token) {
                        originalParams.set('Authorization', token);
                      }

                      const apiUrl = `/api/clients/${clientId}/files/retrieve?${originalParams.toString()}`;

                      // Use plain fetch to avoid global unauthorized redirect logic
                      const resp = await fetch(apiUrl, { method: 'GET' });
                      if (!resp.ok) {
                        console.error(
                          'Download failed',
                          resp.status,
                          resp.statusText
                        );
                        return;
                      }

                      const blob = await resp.blob();
                      // Try to extract filename from content-disposition header
                      const disposition =
                        resp.headers.get('content-disposition') || '';
                      let filename = '';
                      const fileNameMatch =
                        /filename\*?=(?:UTF-8'')?"?([^;"\n]+)"?/i.exec(
                          disposition
                        );
                      if (fileNameMatch && fileNameMatch[1]) {
                        filename = decodeURIComponent(
                          fileNameMatch[1].replace(/"/g, '')
                        );
                      } else {
                        filename =
                          (originalFile || '').split(/[\\/]/).pop() ||
                          'download';
                      }

                      const objectUrl = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = objectUrl;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(objectUrl);
                    } catch (err) {
                      console.error('File download error', err);
                    } finally {
                      // Clear loading state
                      const fileKey = originalFile || href;
                      downloadingFilesRef.current.delete(fileKey);
                      setDownloadingFiles(new Set(downloadingFilesRef.current));
                    }
                  };

                  const fileKey = originalFile || href;
                  const isDownloading = downloadingFiles.has(fileKey);
                  const canPreview =
                    shouldPreview(originalFile) && !isOfficeFile(originalFile);

                  return (
                    <a
                      href={href}
                      onClick={handleClick}
                      style={{
                        color: isDownloading ? '#999' : '#0066cc',
                        textDecoration: 'underline',
                        cursor: isDownloading ? 'not-allowed' : 'pointer',
                        opacity: isDownloading ? 0.6 : 1,
                      }}
                      title={
                        isDownloading
                          ? 'Downloading...'
                          : canPreview
                            ? 'Click to preview'
                            : 'Click to download'
                      }
                    >
                      {isDownloading
                        ? `${children} (downloading...)`
                        : children}
                    </a>
                  );
                }
              } catch (err) {
                console.error('Error parsing copilot-file href', err);
              }

              // Check if it's an example.com link (requires dynamic data fetching)
              if (href && href.includes('example.com')) {
                return <DynamicLink href={href}>{children}</DynamicLink>;
              }

              // Regular link - add default styling
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#0066cc',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {children}
                </a>
              );
            },
            // Handle div elements with artifact-placeholder class
            div: ({ node, ...props }: any) => {
              if (node?.properties) {
                const hasPlaceholder =
                  node.properties.className?.includes?.(
                    'artifact-placeholder'
                  ) ||
                  (Array.isArray(node.properties.className) &&
                    node.properties.className.includes('artifact-placeholder'));

                if (hasPlaceholder) {
                  // Try all possible attribute name formats
                  // rehypeRaw may convert data-* attributes to different formats
                  const id =
                    node.properties.dataId ||
                    node.properties['data-id'] ||
                    node.properties.datatype ||
                    node.properties['data-type'] ||
                    (node.properties['data-*'] && node.properties['data-*'].id);

                  // Debug: log the properties to understand the actual structure
                  // if (process.env.NODE_ENV === 'development') {
                  //   console.log('Artifact placeholder properties:', {
                  //     id,
                  //     type:
                  //       node.properties.dataType ||
                  //       node.properties['data-type'],
                  //     allProps: node.properties,
                  //   });
                  // }

                  if (!id) {
                    return <div {...props} />;
                  }

                  // Find the artifact data
                  const artifact = artifactsData.find((a: any) => a.id === id);
                  if (!artifact) {
                    return <div {...props} />;
                  }

                  return (
                    <ArtifactTag
                      type={
                        artifact.type as
                          | 'html'
                          | 'jsx'
                          | 'react'
                          | 'text'
                          | 'markdown'
                          | 'mermaid'
                          | 'svg'
                          | 'code'
                          | 'isolated-html'
                      }
                      content={artifact.content}
                      title={artifact.title}
                      language={artifact.language}
                    />
                  );
                }
              }

              // Check for isolated HTML marker
              const hasIsolatedHTML =
                node.properties.dataIsolatedHtml ||
                node.properties['data-isolated-html'];

              if (hasIsolatedHTML) {
                const encodedContent =
                  node.properties.dataContent ||
                  node.properties['data-content'];

                if (encodedContent) {
                  try {
                    // 使用浏览器原生 API 进行 base64 解码
                    const htmlContent = decodeURIComponent(
                      escape(atob(encodedContent as string))
                    );

                    return <IsolatedHTMLContainer html={htmlContent} />;
                  } catch (e) {
                    console.error('Error decoding isolated HTML:', e);
                    return <div {...props} />;
                  }
                }
              } // Regular div
              return <div {...props} />;
            },
          }}
        >
          {cleanedContent}
        </ReactMarkdown>
      </div>

      {/* 🔥 FilePreview 已移至 Chat/index.tsx 统一渲染（单例模式）
         点击文件时通过 setFilePreview 设置 store 状态
         blob URL 清理由 useEffect 处理 */}
    </>
  );
}
